import { createSelector } from 'reselect';
import { annotateTaskAvailabilityCodes, convertIntervalToStatus } from './utils';
import { findOrInitializeById } from '../../utils/general';
import moment from 'moment';
import { selectors as taskSelectors } from '../tasks';
import { selectors as availabilityCodeSelectors } from '../availability-codes';
import { selectors as groupSelectors } from '../groups';

const dashboardDateTime = state => state.dashboards.dashboardDatetime.dateTime;

export const loading = state => state.availabilityRequirementSchedules.loading;
export const selectFullSchedulesState = state => state.availabilityRequirementSchedules.full;

// returns a map from taskId to availabilityCodeId for the current availability
// requirement schedules
export const taskToAvailabilityCodeMap = createSelector(
  selectFullSchedulesState,
  taskSelectors.byId,
  availabilityCodeSelectors.byId,
  dashboardDateTime,
  (relevantState, tasks, availabilityCodes, time) => {
    const result = {};
    for (const schedule of Object.values(relevantState)) {
      if (!schedule.intervals) continue;
      // Convert to moment to ensure timezone consistency
      const interval = schedule.intervals.find(i =>
        moment(time).isBetween(moment(i.start_time), moment(i.end_time), null, '[)')
      );
      if (!interval) continue;
      for (const [taskId, availabilityCode] of Object.entries(interval.task_availability_codes)) {
        const codeId = availabilityCode.availability_code_id;
        result[taskId] = {
          task: findOrInitializeById(tasks, taskId),
          availabilityCode: findOrInitializeById(availabilityCodes, codeId)
        };
      }
    }

    return result;
  }
);

// returns a map from availabilityCodeId to all tasks that currently have that
// availability code. Example:
// {359: {
//   task: { id: 359, [otherTaskFields] },
//   availabilityCode: { id: 28, [otherCodeFields] },
// }},
export const availabilityCodeToTasksMap = createSelector(taskToAvailabilityCodeMap, taskToCodeMap => {
  const result = {};

  for (const [_taskId, taskInfo] of Object.entries(taskToCodeMap)) {
    const { task, availabilityCode } = taskInfo;
    const codeId = availabilityCode.id;

    result[codeId] = result[codeId] || { availabilityCode, tasks: [] };
    result[codeId].tasks.push(task);
  }

  return result;
});

// Returns the state of an availability requirement at the specified dateTime
const extractStateAtTime = (arState, dateTime) => {
  dateTime = moment(dateTime);
  let interval = arState.intervals.find(interval =>
    // take the interval that applies right now. Start inclusive, end exclusive
    dateTime.isBetween(moment(interval.start_time), moment(interval.end_time), null, '[)')
  );

  if (!interval) {
    console.log('==========');
    console.log('no interval found! Available intervals', arState.intervals);
    console.log('Requested time', dateTime.format());
    // no interval found which contains dateTime. This could be due to a very
    // small time discrepancy. Search again, but allow for a 15 minute grace
    // period.
    interval = arState.intervals.find(interval => {
      const startTime = moment(interval.start_time).subtract(15, 'minutes');
      // take the interval that applies right now. Start inclusive, end exclusive
      return dateTime.isBetween(startTime, moment(interval.end_time), null, '[)');
    });
    if (interval) {
      console.log('Found fallback by searching 15 minutes ahead', interval);
    } else {
      return {};
    }
  }

  const state = convertIntervalToStatus(interval);

  return {
    availabilityRequirementLevel: state.availabilityRequirementLevel,
    taskAvailabilityCodes: state.taskAvailabilityCodes,
    warningLevel: state.warningLevel,
    serviceLevel: state.serviceLevel,
    skillStatuses: state.skillStatuses,
    timestamp: dateTime.unix()
  };
};

// the current state has a field taskToAvailabilityCode of the shape
// {taskId: availabilityCodeId}
// This method transforms this to:
// {taskId: { task: [taskObject], availabilityCode: [availabilityCodeObject] }}
export const annotateAllTaskAvailabilityCodes = (currentState, tasks, codes) => {
  if (!currentState || Object.keys(currentState).length === 0) return {};
  const result = {};
  for (const [id, currentArState] of Object.entries(currentState)) {
    result[id] = annotateTaskAvailabilityCodes(currentArState, tasks, codes);
  }
  return result;
};

// Returns the current state per availability requirement (at t = now)
export const selectCurrentState = createSelector(
  state => state.availabilityRequirementSchedules.currentState.now,
  taskSelectors.byId,
  availabilityCodeSelectors.byId,
  (currentState, tasks, codes) => annotateAllTaskAvailabilityCodes(currentState, tasks, codes)
);

// Returns the previous state per availability requirement
export const selectPreviousState = createSelector(
  state => state.availabilityRequirementSchedules.currentState.previous,
  taskSelectors.byId,
  availabilityCodeSelectors.byId,
  (currentState, tasks, codes) => annotateAllTaskAvailabilityCodes(currentState, tasks, codes)
);

// Returns the state of each availability requirement at the dashboard time
export const selectDashboardState = createSelector(
  selectFullSchedulesState,
  taskSelectors.byId,
  availabilityCodeSelectors.byId,
  dashboardDateTime,
  (fullState, tasks, codes, dateTime) => {
    const stateAtDashboardTime = {};
    // From the full availability requirement schedules, extract the state of
    // each availability requirement for the dashboardDateTime
    for (const [id, arState] of Object.entries(fullState)) {
      stateAtDashboardTime[id] = extractStateAtTime(arState, dateTime);
    }
    return annotateAllTaskAvailabilityCodes(stateAtDashboardTime, tasks, codes);
  }
);

const findGroupAndAvailabilityRequirement = (groups, arId) => {
  // find groups that are associated to this availability requirement
  const matchingGroups = groups.filter(g => g.availability_requirements.some(ar => Number(ar.id) === Number(arId)));

  // bottom up: first look for stations, then for clusters, finally for
  // regions
  const group =
    matchingGroups.find(g => g.type == 'station') ||
    matchingGroups.find(g => g.type === 'cluster') ||
    matchingGroups.find(g => g.type === 'region');

  if (!group) return {};

  const availabilityRequirement = group.availability_requirements.find(ar => Number(ar.id) === Number(arId));

  return { group, availabilityRequirement };
};

// This is used in the StateChangedPopup for displaying changes to availability
// requirements. Whenever there is a change to the warning level, service level
// or the composite availability requirement level, the popup should show
export const selectSignificantChanges = createSelector(
  selectPreviousState,
  selectCurrentState,
  groupSelectors.values,
  (previousArState, currentArState, groups) => {
    const result = {};
    for (const [id, currentState] of Object.entries(currentArState)) {
      const previousState = previousArState[id];
      if (!previousState) continue;

      const { timestamp } = currentState;
      const oldLevel = previousState.availabilityRequirementLevel || {};
      const newLevel = currentState.availabilityRequirementLevel || {};
      const oldServiceLevel = previousState.serviceLevel;
      const newServiceLevel = currentState.serviceLevel;
      const oldWarningLevel = previousState.warningLevel;
      const newWarningLevel = currentState.warningLevel;

      const significantWarningLevelUpdate =
        (oldWarningLevel === 'below_required' && newWarningLevel !== 'below_required') ||
        (oldWarningLevel !== 'below_required' && newWarningLevel === 'below_required');

      const significantChange =
        oldLevel.id !== newLevel.id || oldServiceLevel !== newServiceLevel || significantWarningLevelUpdate;

      if (!significantChange) continue;

      const { group, availabilityRequirement } = findGroupAndAvailabilityRequirement(groups, id);
      if (!availabilityRequirement) continue;

      result[id] = {
        id: Number(id),
        hash: `${id}_${timestamp}`,
        timestamp,
        group,
        availabilityRequirement,
        previousState,
        currentState
      };
    }

    return result;
  }
);
